import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router';

import { useAccountTokenStore } from '@/entities/account-session';
import { useMyAppointments } from '@/entities/appointment';
import { useMyMessages } from '@/entities/clinic-message';
import { useConsultReplies } from '@/features/watch-consult-replies';
import { toLocaleDigits } from '@/shared/lib/format';

import { TABS } from '../model/tabs';

/**
 * The patient's primary navigation, pinned to the bottom edge.
 *
 * Bottom, not top, for one reason: this product is used one-handed on a phone,
 * often by someone standing in a corridor, and the bottom of the screen is the
 * only part of a large phone a thumb reaches without regripping.
 *
 * It is a translucent material the content scrolls *under* rather than an
 * opaque strip that eats a slice of the viewport, and it clears the home
 * indicator via the safe-area inset (§materials). On wide screens it moves to
 * the top edge of the content column instead of stretching across a desktop.
 *
 * Labels are always visible, never icon-only: an unlabelled glyph is a
 * guessing game, and a fair share of these patients are older.
 */
/** A minute: a reply is worth seeing soon, and this runs on every patient screen. */
const PATIENT_POLL_MS = 60_000;

export function TabBar(): JSX.Element {
  const { t, i18n } = useTranslation('common');

  // «پیام تازه» — asked only with a session, on the same keys the messages page
  // reads, so opening that page clears this count in the same render. Two
  // sources: the clinic's notices, and a doctor's answer to a پیگیری the
  // patient has not opened — which the «پیام‌ها» page lists too. Polled, so
  // an answer shows up while the app is open and not only on the next visit.
  const signedIn = useAccountTokenStore((state) => state.token !== null);
  const messages = useMyMessages({ enabled: signedIn, refetchInterval: PATIENT_POLL_MS });
  const appointments = useMyAppointments({ enabled: signedIn, refetchInterval: PATIENT_POLL_MS });
  const unseenReplies = (appointments.data ?? []).filter(
    (appointment) => appointment.followUp?.hasUnseenReply === true,
  ).length;
  const unread = signedIn ? (messages.data?.unreadCount ?? 0) + unseenReplies : 0;
  // «مشاوره» carries its own count: the doctor's unread answers there
  const consultReplies = useConsultReplies().count;
  const badgeOf = (key: string): number =>
    key === 'messages' ? unread : key === 'consult' ? consultReplies : 0;

  return (
    <nav
      aria-label={t('nav.label')}
      className="fixed inset-x-0 bottom-0 z-40 material-tabbar sm:mx-auto sm:mb-4 sm:max-w-md sm:rounded-[var(--radius-xl)] sm:border sm:border-border sm:shadow-lg"
    >
      <ul className="mx-auto flex w-full max-w-md items-stretch justify-around px-1">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const badge = badgeOf(tab.key);

          return (
            <li className="flex-1" key={tab.key}>
              <NavLink
                className={({ isActive }): string =>
                  [
                    'group flex touch-manipulation flex-col items-center justify-center gap-1',
                    'min-h-[var(--tab-bar-height)] rounded-[var(--radius-md)] px-1 pt-1.5 pb-1 select-none',
                    // the press is acknowledged on pointer-down, not on release
                    'transition-transform duration-[var(--duration-instant)]',
                    'ease-[var(--ease-out)] active:scale-[0.92]',
                    isActive ? 'text-primary-ink' : 'text-muted',
                  ].join(' ')
                }
                // `/` would otherwise match every address in the app
                end={tab.path === '/'}
                to={tab.path}
              >
                {({ isActive }): JSX.Element => (
                  <>
                    {/*
                      The active tab's icon sits on a tinted pill. State is
                      never colour alone — the pill is a second, non-chromatic
                      cue, and the label below carries the meaning in words.
                    */}
                    <span
                      className={[
                        'flex items-center justify-center rounded-full px-3.5 py-1',
                        'transition-[background-color,transform]',
                        'duration-[var(--duration-fast)] ease-[var(--ease-out)]',
                        isActive ? 'bg-primary-tint' : 'bg-transparent',
                      ].join(' ')}
                    >
                      <Icon className={isActive ? 'size-6' : 'size-5.5'} />
                      {badge > 0 ? (
                        <span className="relative">
                          <span
                            aria-hidden="true"
                            className="absolute -top-4 -end-2 flex min-w-5 items-center justify-center rounded-full bg-primary px-1 type-caption font-semibold tabular-nums text-on-primary"
                          >
                            {toLocaleDigits(String(badge), i18n.language)}
                          </span>
                          <span className="sr-only">
                            {t(tab.key === 'consult' ? 'nav.newReplies' : 'nav.unread', {
                              count: badge,
                              n: toLocaleDigits(String(badge), i18n.language),
                            })}
                          </span>
                        </span>
                      ) : null}
                    </span>

                    <span className={`type-caption ${isActive ? 'font-semibold' : 'font-normal'}`}>
                      {t(`nav.${tab.key}`)}
                    </span>
                  </>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
